import React from "react";
import { Link as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  Breadcrumbs,
  Link,
  SelectChangeEvent,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import AddIcon from "../../assets/images/add-icon-white.svg";
import EditableTextField from "../../components/EditableTextField";
import AppSelect from "../../components/AppSelect";
import { Recording } from "../../declarations/interface";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: `${theme.spacing(3)} ${theme.spacing(4.5)} ${theme.spacing(1.5)}`,
  },
  heading: {
    fontWeight: 700,
    fontSize: 20,
    letterSpacing: "-4%",
    lineHeight: "24px",
  },
  separator: {
    marginLeft: `${theme.spacing(0.5)} !important`,
    marginRight: `${theme.spacing(0.5)} !important`,
  },
}));

const CustomButton: React.FC<{
  routeTo?: string;
  handleClick?: () => void;
  title: string;
  variant: "outlined" | "contained";
  color: "primary" | "secondary";
  disabled: boolean;
}> = ({ routeTo, handleClick, title, variant, color, disabled }) => {
  return (
    <Button
      variant={variant}
      color={color}
      sx={{
        padding: "10px 21px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
        textTransform: "unset !important",
        marginRight: "8px",
        marginLeft: "8px",
      }}
      {...(routeTo ? { to: routeTo, component: RouterLink } : {})}
      {...(handleClick ? { onClick: handleClick } : {})}
      aria-label={title}
      disabled={disabled}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          lineHeight: "16px",
        }}
      >
        {title}
      </Typography>
    </Button>
  );
};

const Header: React.FC<{
  projectId: string | number;
  projectName: string;
  recordingName: string;
  selectedRecordingId: string;
  recordings: Recording[];
  selectRecording: (recordingId: string) => void;
  onRecordingNameUpdate: (updatedName: string) => void;
  handleSave: () => void;
  saveEnabled: boolean;
}> = ({
  projectId,
  projectName,
  recordingName,
  onRecordingNameUpdate,
  selectedRecordingId,
  selectRecording,
  recordings,
  handleSave,
  saveEnabled,
}) => {
  const classes = useStyles();

  const handleRecordingChange = (event: SelectChangeEvent<string>) => {
    selectRecording(event.target.value);
  };
  return (
    <Box className={classes.root}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2.5}
      >
        <Box>
          <RecordingBreadcrumb
            projectId={projectId}
            projectName={projectName}
            recordingName={recordingName}
            onRecordingNameUpdate={onRecordingNameUpdate}
          />
        </Box>
        <Box>
          <CustomButton
            variant="outlined"
            color="primary"
            routeTo={`/project/${projectId}`}
            title="Cancel"
            disabled={false}
          />
          <CustomButton
            variant="contained"
            color="secondary"
            title="Save"
            handleClick={handleSave}
            disabled={!saveEnabled}
          />
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <AppSelect
            value={selectedRecordingId}
            onChange={handleRecordingChange}
            options={recordings.map((recording: Recording) => ({
              label: recording.recording_name,
              value: recording.recording_id,
            }))}
            id={`select-recording-dropdown`}
          />
        </Box>
      </Box>
    </Box>
  );
};

const RecordingBreadcrumb: React.FC<{
  projectId: string | number;
  projectName: string;
  recordingName: string;
  onRecordingNameUpdate: (value: string) => void;
}> = ({ projectId, projectName, recordingName, onRecordingNameUpdate }) => {
  const classes = useStyles();
  return (
    <Breadcrumbs classes={{ separator: classes.separator }}>
      <Link
        color="inherit"
        to={`/project/${projectId}`}
        component={RouterLink}
        underline="none"
      >
        <Typography
          variant="body1"
          color="primary"
          sx={{ textTransform: "capitalize" }}
        >
          {projectName}
        </Typography>
      </Link>
      <EditableTextField
        value={recordingName}
        onSubmit={onRecordingNameUpdate}
        className={classes.heading}
      />
    </Breadcrumbs>
  );
};

Header.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  projectName: PropTypes.string.isRequired,
  recordingName: PropTypes.string.isRequired,
  onRecordingNameUpdate: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  selectedRecordingId: PropTypes.string.isRequired,
  selectRecording: PropTypes.func.isRequired,
  recordings: PropTypes.array.isRequired,
  saveEnabled: PropTypes.bool.isRequired,
};

export default Header;
